import { useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import CardContent from '@material-ui/core/CardContent';
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import BlueFullWidthButton from 'components/BlueFullWidthButton/BlueFullWidthButton';

import Company from '../../../../types/Company';
import { StyledCard, StyledTitle } from './styled';

ChartJS.register(
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

interface Props {
    company: Company;
    updateData: () => void;
}

const chartOptions = {
    responsive: true,
    plugins: {
        legend: {
            display: false
        },
        tooltip: {
            callbacks: {
                label: (item: any) => {
                    return `${item.label}: ${item.formattedValue}`;
                },
            },
        },
    },
    scales: {
        x: {
            display: false,
            stacked: true,
            ticks: {
                display: false
            }
        },
        y: {
            display: false,
            stacked: true,
            ticks: {
                display: false
            }
        },
    },
};

const Card6 = (props: Props) => {
    const { company } = props;
    const { chartData } = company;

    const { t } = useTranslation();
    const history = useHistory();

    const thisChartData = useMemo(() => {
        const labels = [t('pages.dashboard.cards.kpi.chart.updated'), t('pages.dashboard.cards.kpi.chart.missing')];

        const colors = [
            {
                r: 110,
                g: 168,
                b: 218
            },
            {
                r: 226,
                g: 110,
                b: 11
            }
        ];

        const datasets = [
            {
                label: '',
                data: [chartData.kpi.total - chartData.kpi.missingData, chartData.kpi.missingData],
                backgroundColor: colors.map((c: any) => {
                    return 'rgba(' + c.r + ', ' + c.g + ', ' + c.b + ', 1)';
                }),
            }
        ];

        return {
            labels,
            datasets,
        };
    }, [chartData, t]);

    const count = chartData.kpi.missingData;

    return (
        <StyledCard variant='outlined'>
            <CardContent style={{ height: '100%', minHeight: '300px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div>
                        <StyledTitle style={{ fontSize: '1.65rem', fontWeight: 500, marginBottom: '4px' }}>
                            {t('pages.dashboard.cards.kpi.title')}
                        </StyledTitle>

                        {chartData.currentAssessmentState === 'certified' ? (
                            <StyledTitle style={{ fontSize: '1rem', marginTop: '16px', marginBottom: '24px' }}>
                                <Trans i18nKey='pages.dashboard.duplicate_pa_hint' />
                            </StyledTitle>
                        ) : (
                            <>
                                <StyledTitle style={{ fontSize: '1rem' }}>
                                    <Trans i18nKey='pages.dashboard.cards.kpi.message' count={count} >
                                        {/*
                                _<span style={{display: 'block', fontWeight: 'bold', margin: '8px 0'}}>{{count}}</span>_
                                */}
                                        _<strong>{{ count }}</strong>_
                                    </Trans>
                                </StyledTitle>

                                <Pie
                                    data={thisChartData}
                                    options={chartOptions}
                                    style={{ maxWidth: '80%', maxHeight: '150px', margin: '24px auto 0' }}
                                />
                            </>
                        )}
                    </div>

                    <div style={{ flexGrow: 1 }} />

                    <BlueFullWidthButton variant='contained' color='primary' style={{ width: 'calc(100% + 44px)', margin: '24px -22px -18px' }} onClick={() => {
                        history.push('/plastic-assessments/' + chartData.currentAssessmentId + '/kpi');
                    }} disabled={!chartData.currentAssessmentId || chartData.currentAssessmentState === 'certified'}>
                        {t('pages.dashboard.cards.kpi.button')}
                    </BlueFullWidthButton>
                </div>
            </CardContent>
        </StyledCard>
    );
};

export default Card6;
